<template>
    <div>
        <div class="engagement-title">
            Engagement Report
        </div>
        <div class="engagement-sub-title">
            Last 30 Days
        </div>
        <div v-if="data_loading">
            <ds-placeholder :rows="rows" />
        </div>

        <ds-table-grid
            v-if="thereAreEngagementResults"
            :limit="limit"
            :data="engagement"
            data-qa="engagement-report"
            class="engagement-table"
            :sort-ascending="sortAscending"
            :sort-field.sync="sortField"
            @sort="sort"
        >
            <ds-table-grid-column
                label="Full Name"
                prop="fullName"
                width="180"
            >
                <template v-slot="form">
                    <a :href="'/Contact/manageContact.jsp?view=edit&ID=' + String(form.contactId)" target="_blank">
                        <span>{{ checkNoName(form.fullName) }}</span>
                    </a>
                </template>
            </ds-table-grid-column>

            <ds-table-grid-column
                label="Email"
                prop="emailAddress"
                width="200"
            />

            <ds-table-grid-column
                label="Contact ID"
                prop="contactId"
                width="100"
            />

            <ds-table-grid-column
                label="# Emails Sent"
                prop="numberOfEmailsSent"
                width="100"
            />

            <ds-table-grid-column
                label="Days Since Last Engagement"
                prop="daysSinceLastEngagement"
                width="250"
                sortable
            >
                <template v-slot="form">
                    <span> {{ neverEngaged(form.daysSinceLastEngagement) }} </span>
                </template>
            </ds-table-grid-column>

            <ds-table-grid-column
                label="Last Email Sent"
                prop="mostRecentSend"
                width="250"
            >
                <template v-slot="form">
                    <span> {{ dateFormatDate(form.mostRecentSend) }} </span>
                </template>
            </ds-table-grid-column>

            <ds-table-grid-column
                label="Last Open Date"
                prop="mostRecentOpen"
                width="250"
                sortable
            >
                <template v-slot="form">
                    <span> {{ dateFormatDate(form.mostRecentOpen) }} </span>
                </template>
            </ds-table-grid-column>

            <ds-table-grid-column
                label="Last Clicked Date"
                prop="mostRecentClick"
                width="250"
                sortable
            >
                <template v-slot="form">
                    <span> {{ dateFormatDate(form.mostRecentClick) }} </span>
                </template>
            </ds-table-grid-column>
        </ds-table-grid>

        <div v-if="!thereAreEngagementResults && !loading" class="empty-state-message">
            <ds-inline-alert leading-icon type="subtle">
                {{ getEmptyStateMessage }}
            </ds-inline-alert>
        </div>

        <footer v-if="thereAreEngagementResults" class="pagination-footer">
            <pagination-control
                :total-count="totalCount"
                :limit="limit"
                :offset="offset"
                @page="updatePage"
            />

            <div class="page-size">
                <ds-select-field
                    v-model="limit"
                    class="limit-select"
                    dense
                    bind-value-only
                    :options="pageSizes"
                />
                Per Page
            </div>
        </footer>
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import PaginationControl from '../shared/PaginationControl';
import { PAGINATION_SIZE } from '@/utils/email.util';

const EMPTY_STATE_MESSAGE = 'No email engagement has been recorded for the current reporting period';

export default {
    components: {
        PaginationControl,
    },

    props: {
        timezone: String,
        offset: {
            type: Number,
            default: 0,
        },
        stacked: Boolean,
    },

    data() {
        return {

            pageSizes: [
                { label: '25', value: 25 },
            ],
            allEngagement: [],
            pages: 0,
            sortAscending: false,
            sortField: 'daysSinceLastEngagement',
            queryIDLookup: {},
            language: navigator.language || navigator.userLanguage,
            limit: PAGINATION_SIZE,
            totalCount: 0,
            rowSelection: null,
            OrderFields: {
                daysSinceLastEngagement: 'ENGAGEMENT_DAYS',
                mostRecentOpen: 'MOST_RECENT_OPEN',
                mostRecentClick: 'MOST_RECENT_CLICK',
            },
            OrderDirection: {
                true: 'ASC',
                false: 'DESC',
            },
            numberOfEmailsSent: 0,
            loading: true,
            rows: [
                { height: '2rem', boxes: [1] },
                { height: '2rem', boxes: [1] },
                { height: '2rem', boxes: [1] },
            ],
        };
    },

    created() {
        this.rowSelection = 'single';
        this.load();
    },
    watch: {
        offset() {
            this.loadEngagement();
        },

        limit() {
            this.loadEngagement();
        },
    },

    computed: {
        engagement() {
            return this.allEngagement;
        },

        tenantTimeZone() {
            return this.timezone;
        },

        thereAreEngagementResults() {
            return !this.loading && Boolean(this.allEngagement.length);
        },

        data_loading() {
            return this.loading;
        },

        getEmptyStateMessage() {
            return EMPTY_STATE_MESSAGE;
        },
    },

    methods: {

        dateFormatDate (date) {
            if (!date) {
                return '<no date>';
            }

            const formattedDate = moment.utc(date).tz(this.tenantTimeZone);

            formattedDate.locale(this.language);

            return moment(formattedDate).format('llll');
        },

        neverEngaged(engagementDays) {
            if (engagementDays < 0) {
                return 'Never engaged';
            }

            return engagementDays;
        },

        checkNoName(name) {
            if (!name) {
                return '<no name>';
            }

            return name;
        },

        updatePage({ offset }) {
            this.offset = offset;
        },

        sort(field) {
            this.sortField = field.field;
            this.sortAscending = field.ascending;
            this.loadEngagement();
        },

        async load() {
            this.loadEngagement();
        },

        async loadEngagement() {
            try {
                // TODO: StartTime, EndTime to come from UI components EO-4456
                this.loading = true;
                const key = ''.concat('SortDirection_',
                    String(this.OrderDirection[this.sortAscending]), '__',
                    'SortField_',
                    String(this.OrderFields[this.sortField]));

                const theQueryId = (key in this.queryIDLookup) ? this.queryIDLookup[key] : null;

                const params = {
                    pageIndex: Math.floor(this.offset / PAGINATION_SIZE),
                    startTime: null,
                    endTime: null,
                    queryId: theQueryId,
                    engagementOrderBy: this.OrderFields[this.sortField],
                    sortOrder: this.OrderDirection[this.sortAscending],
                };

                const engagementResponse = await axios.get('/app/reportcentral/getEngagement',
                    { params });

                this.allEngagement = engagementResponse.data.engagement;
                this.totalCount = engagementResponse.data.totalSize;
                this.loading = false;

                // Add the current queryID to the lookup so we can use it when we pull next page
                if (!(key in this.queryIDLookup)) {
                    this.queryIDLookup[key] = engagementResponse.data.queryId;
                }
            } catch (e) {
                this.allEngagement = [];
                this.totalCount = 0;
                this.loading = false;
                this.$error({ message: 'Unable to get engagement' });
            }
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>

.empty-state-message {
    width: 100%;
    display: flex;
    flex-direction: column;
}

footer {
    padding-top: $spacing-100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: inset 0 1px 0 $color-gray-200;

    @media($small) {
        grid-template-columns: 100%;
        justify-content: center;
        text-align: center;
    }

    .page-size {
        display: grid;
        grid-template-columns: px-to-rem(80) auto;
        align-items: center;
        grid-gap: $spacing-100;

        @media($small) { display: none }
    }
}

</style>


<style lang="scss" rel="stylesheet/scss">
.engagement-title{
    display: flex;
    font-size: large;
    justify-content: center;
    width: 100%;
    height: $spacing-250;
}

.engagement-sub-title{
    display: flex;
    font-size: small;
    justify-content: center;
    width: 100%;
    height: $spacing-250;
}

.engagement-table {
    .table-wrapper {
        height: calc(100vh - #{px-to-rem(349)});
        width: 100%;
        overflow-y: auto;

        @media(max-width: 749px) {
            height: calc(100vh - #{px-to-rem(355)});
        }
    }

    thead {
        background: $color-white;
        z-index: 2;
        border-top: none;
        box-shadow: inset 0 -1px 0 $color-gray-200;
    }
}
</style>
